import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, UploadCloud, FileSearch, PenLine, SearchCheck, PackageCheck } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { ALL_LANDING_PAGES, LANGUAGE_PAGES, INDUSTRY_PAGES } from "@/data/landingPages";

const STEPS = [
  { icon: UploadCloud, t: "Upload", d: "Send your documents securely" },
  { icon: FileSearch, t: "Quote", d: "Receive a clear quotation" },
  { icon: PenLine, t: "Translate", d: "Specialist linguist assigned" },
  { icon: SearchCheck, t: "Review", d: "Independent quality review" },
  { icon: PackageCheck, t: "Deliver", d: "Secure delivery on time" },
];

export default function LandingPage() {
  const { slug } = useParams();
  const page = LANGUAGE_PAGES[slug] || INDUSTRY_PAGES[slug];

  useEffect(() => {
    if (!page) return;
    document.title = `${page.title} | TranslateIt`;
    let meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", page.meta);
    const schema = document.createElement("script");
    schema.type = "application/ld+json";
    schema.id = "landing-schema";
    schema.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      name: page.title,
      provider: { "@type": "Organization", name: "TranslateIt" },
      description: page.meta,
    });
    document.head.appendChild(schema);
    return () => { document.getElementById("landing-schema")?.remove(); };
  }, [page]);

  if (!page) return <Navigate to="/" replace />;

  return (
    <div data-testid={`landing-${slug}`} className="bg-[#FAF8F5] min-h-screen">
      <Header />
      <main>
        <section className="relative overflow-hidden pt-[72px]">
          <div className="absolute inset-0 hero-grid-bg" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-16 pb-20 lg:pt-24">
            <motion.p
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="text-xs font-mono uppercase tracking-[0.2em] text-[#C87A4B] mb-5"
            >
              {page.type === "language" ? "Language Pair" : "Specialist Service"}
            </motion.p>
            <div className="overflow-hidden">
              <motion.h1
                initial={{ y: "110%" }} animate={{ y: 0 }}
                transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
                className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08] text-[#0B111E] max-w-3xl"
              >
                {page.title}
              </motion.h1>
            </div>
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }}
              className="mt-6 text-base sm:text-lg leading-relaxed text-slate-600 max-w-2xl"
            >
              {page.intro}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.7 }}
              className="mt-8"
            >
              <Link
                to="/#quote"
                data-testid="landing-quote-btn"
                className="inline-flex items-center gap-2 bg-[#C87A4B] hover:bg-[#B3673A] text-white font-heading font-bold text-lg px-8 py-4 rounded-lg shadow-lg shadow-[#C87A4B]/25 transition-all duration-200 hover:-translate-y-0.5"
              >
                Get a Quote <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C87A4B] mb-4">What we translate</p>
              <ul className="flex flex-col gap-4">
                {page.points.map((p, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.5 }}
                    className="flex items-start gap-3 text-base text-slate-700"
                  >
                    <CheckCircle2 className="w-5 h-5 text-[#C87A4B] shrink-0 mt-0.5" strokeWidth={1.8} />
                    {p}
                  </motion.li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C87A4B] mb-6">How it works</p>
              <div className="flex flex-col gap-5">
                {STEPS.map((s, i) => (
                  <motion.div
                    key={s.t}
                    initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }} transition={{ delay: i * 0.07, duration: 0.5 }}
                    className="flex items-center gap-4"
                  >
                    <span className="w-10 h-10 rounded-lg bg-[#FAF8F5] border border-slate-200 flex items-center justify-center text-[#C87A4B] shrink-0">
                      <s.icon className="w-5 h-5" strokeWidth={1.7} />
                    </span>
                    <div>
                      <p className="font-heading font-semibold text-[#0B111E] text-sm">{i + 1}. {s.t}</p>
                      <p className="text-xs text-slate-500">{s.d}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white border-y border-slate-200">
          <div className="max-w-3xl mx-auto px-4 sm:px-8 py-16">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C87A4B] mb-4 text-center">Common questions</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-[#0B111E] text-center mb-8">
              {page.title} — FAQ
            </h2>
            <Accordion type="single" collapsible>
              {page.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`landing-faq-${i}`}>
                  <AccordionTrigger className="text-left font-semibold text-[#0B111E]">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16">
          <div className="rounded-2xl bg-[#0B111E] text-white px-8 py-12 text-center">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-3">Ready to translate?</h2>
            <p className="text-slate-400 text-sm mb-7">Upload your documents and receive your quotation.</p>
            <Link
              to="/#quote"
              data-testid="landing-final-cta-btn"
              className="inline-flex items-center gap-2 bg-[#C87A4B] hover:bg-[#B3673A] text-white font-heading font-bold px-8 py-4 rounded-lg transition-all duration-200 hover:-translate-y-0.5"
            >
              Get a Quote <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export { ALL_LANDING_PAGES };
